import { oc } from "@orpc/contract";
import { z } from "zod";
import { OptionalOffsetPageShape } from "../../schemas/common";

// The override browser: every live procurement override of one kind (lead-time
// pins, inventory-policy min/max/eligibility pins, or forecast growth/shrinkage
// factors), overdue reviews first. Read-only: a row links back to the product
// page where it can be extended, edited or revoked. Plans:
// docs/procurement/lead-time-overrides.md,
// docs/procurement/growth-shrinkage-factors.md.

export const OVERRIDE_KINDS = [
  "lead_time",
  "inventory_policy",
  "forecast_factor",
] as const;
export const OverrideKindSchema = z.enum(OVERRIDE_KINDS);
export type OverrideKind = z.infer<typeof OverrideKindSchema>;

/**
 * Where the row stands against its advisory review date: past it, ahead of
 * it, or carrying none (an override with an expiry needs no review date).
 */
export const OVERRIDE_REVIEW_STATUSES = [
  "overdue",
  "upcoming",
  "none",
] as const;
export const OverrideReviewStatusSchema = z.enum(OVERRIDE_REVIEW_STATUSES);
export type OverrideReviewStatus = z.infer<typeof OverrideReviewStatusSchema>;

/**
 * Whether the row's window has started. A scheduled row is live (unrevoked,
 * not lapsed) but not yet applying; for a growth factor the forecast horizon
 * already reaches its months, so "scheduled" reads as "later months only".
 */
export const OVERRIDE_LIFECYCLES = ["active", "scheduled"] as const;
export const OverrideLifecycleSchema = z.enum(OVERRIDE_LIFECYCLES);
export type OverrideLifecycle = z.infer<typeof OverrideLifecycleSchema>;

export const OverrideBrowserRowSchema = z.object({
  kind: OverrideKindSchema,
  /** The override row's id in its own table (see `kind`). */
  id: z.string().uuid(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  /** ProductV3 key (the SKU); procurement carries no legacy v2 identity. */
  productV3Key: z.string().nullable(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  /** Set for `lead_time` rows. */
  leadTimeDays: z.number().int().nullable(),
  /** Set (where pinned) for `inventory_policy` rows. */
  minQuantity: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  autoTransferEligible: z.boolean().nullable(),
  autoBuyEligible: z.boolean().nullable(),
  /** Set for `forecast_factor` rows: the multiplier on the forecast (1.15 = +15%). */
  factor: z.number().nullable(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  reviewAt: z.string().nullable(),
  reviewStatus: OverrideReviewStatusSchema,
  lifecycle: OverrideLifecycleSchema,
  reason: z.string().nullable(),
  createdAt: z.string(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
});
export type OverrideBrowserRow = z.infer<typeof OverrideBrowserRowSchema>;

export const ListOverridesInputSchema = z.object({
  kind: OverrideKindSchema,
  ...OptionalOffsetPageShape,
});

export const ListOverridesResponseSchema = z.object({
  /**
   * Overdue reviews first (longest overdue at the top), then upcoming reviews
   * (soonest first), then rows with no review date (newest first).
   */
  rows: z.array(OverrideBrowserRowSchema),
  total: z.number().int(),
});

/** Live overrides past their review date, per kind: the browser's tab counts. */
export const OverrideOverdueCountsSchema = z.object({
  leadTime: z.number().int(),
  inventoryPolicy: z.number().int(),
  forecastFactor: z.number().int(),
});

export type ListOverridesInput = z.infer<typeof ListOverridesInputSchema>;
export type ListOverridesResponse = z.infer<typeof ListOverridesResponseSchema>;
export type OverrideOverdueCounts = z.infer<typeof OverrideOverdueCountsSchema>;

export const overridesContracts = {
  list: oc
    .input(ListOverridesInputSchema)
    .output(ListOverridesResponseSchema)
    .route({
      method: "GET",
      summary: "List live procurement overrides",
      description:
        "Read-only list of active or scheduled overrides of one kind (lead time, inventory policy or forecast growth factor), overdue reviews first, with SKU, warehouse and reason.",
    }),
  overdueCounts: oc
    .input(z.object({}))
    .output(OverrideOverdueCountsSchema)
    .route({
      method: "GET",
      summary: "Count overdue procurement override reviews",
      description:
        "Returns how many live lead-time, inventory-policy and forecast-factor overrides are past their review date, per kind.",
    }),
} as const;
